import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Segment, Header, Timeline, Icon, Label, Table, Message, Loader, Button } from 'semantic-ui-react';
import { Link, withRouter } from 'react-router-dom';
import MultiplyHeader from '../components/MultiplyHeader/MultiplyHeader';
import { componentContainerStyle, componentContentStyle } from '../constants';

class MeetingTracePage extends Component {
  state = {
    loading: true,
    meeting: null,
    timeline: [],
    error: null
  };

  componentDidMount() {
    this.loadMeetingTrace();
  }

  loadMeetingTrace = async () => {
    const { match, meetingsStore } = this.props;
    const meetingId = match.params.meetingId;

    this.setState({ loading: true, error: null });

    try {
      let meeting = null;
      let timeline = [];

      await meetingsStore.loadMeetings({ limit: 1000 });

      meeting = meetingsStore.meetings.find(m =>
        (m.id === meetingId) || (m.botId === meetingId)
      );

      if (!meeting) {
        this.setState({
          loading: false,
          error: `Meeting ${meetingId} not found`
        });
        return;
      }

      // Build timeline from meeting data
      timeline = this.buildTimeline(meeting);

      this.setState({ loading: false, meeting, timeline });
    } catch (error) {
      console.error('Failed to load meeting trace:', error);
      this.setState({ loading: false, error: error.message });
    }
  };

  buildTimeline = (meeting) => {
    const timeline = [];
    const status = meeting.status;

    // 1. Scheduled
    if (meeting.createdAt) {
      timeline.push({
        stage: 'scheduled',
        icon: 'calendar',
        color: 'blue',
        title: 'Meeting Scheduled',
        timestamp: meeting.createdAt,
        details: {
          'Meeting Title': meeting.meetingTitle,
          'Meeting URL': meeting.meetingUrl,
          'Scheduled Start': meeting.scheduledStartTime,
          'Created Via': meeting.approvalRequestId ? 'Approval Request' : 'Calendar Webhook',
          'iCal UID': meeting.icalUid,
          'Calendar Event ID': meeting.calendarEventId
        }
      });
    }

    // 2. Recording - show if we have a recall bot ID or status is past scheduled
    if (meeting.recallBotId && ['recording', 'transcription', 'transcription_complete', 'identified', 'needs_triage', 'assigned', 'analyzing', 'delivered', 'completed'].includes(status)) {
      timeline.push({
        stage: 'recording',
        icon: 'video',
        color: 'red',
        title: 'Bot Joined & Recording Started',
        timestamp: meeting.recordingStartedAt || meeting.scheduledStartTime,
        details: {
          'Recall Bot ID': meeting.recallBotId || 'N/A',
          'Recording Status': meeting.recordingStatus || 'Completed'
        }
      });
    }

    // 3. Recording Complete - only if we have actual evidence
    if (meeting.recordingCompletedAt || meeting.recordingId ||
        (meeting.metadata && meeting.metadata.recordingReady)) {
      timeline.push({
        stage: 'recording_complete',
        icon: 'check circle',
        color: 'green',
        title: 'Recording Completed',
        timestamp: meeting.recordingCompletedAt || meeting.updatedAt,
        details: {
          'Recording ID': meeting.recordingId || 'N/A',
          'Duration': meeting.duration ? `${meeting.duration} minutes` : 'N/A',
          'Media URL': meeting.metadata?.recordingUrl ? 'Available' : 'N/A'
        }
      });
    }

    // 4. Transcription Started - only if transcription actually started
    if (status === 'transcribing' || meeting.transcriptionJobName ||
        meeting.transcriptionStartedAt ||
        ['transcription_completed', 'identified', 'needs_triage', 'assigned',
         'analyzing', 'delivered'].includes(status)) {
      timeline.push({
        stage: 'transcription',
        icon: 'file text',
        color: status === 'transcribing' ? 'purple' : 'grey',
        title: 'Transcription Started',
        timestamp: meeting.transcriptionStartedAt || meeting.updatedAt,
        details: {
          'Service': 'AWS Transcribe',
          'Job Name': meeting.transcriptionJobName || 'N/A',
          'Status': status === 'transcribing' ? 'In Progress' : 'Completed'
        }
      });
    }

    // 5. Transcription Complete - only if we have actual transcription
    if (status === 'transcription_completed' ||
        meeting.transcriptionCompletedAt || meeting.transcriptS3Key ||
        ['identified', 'needs_triage', 'assigned', 'analyzing', 'delivered'].includes(status)) {
      timeline.push({
        stage: 'transcription_complete',
        icon: 'file alternate',
        color: 'violet',
        title: 'Transcription Completed',
        timestamp: meeting.transcriptionCompletedAt || meeting.updatedAt,
        details: {
          'Duration': meeting.duration ? `${meeting.duration}s` : 'N/A',
          'Speakers': meeting.speakers || 'N/A',
          'Transcript Key': meeting.transcriptS3Key || 'N/A'
        }
      });
    }

    // 6. Participant Identification - only if participants were actually identified
    if (status === 'identified' || meeting.identifiedAt ||
        meeting.assignmentStatus ||
        ['needs_triage', 'assigned', 'analyzing', 'delivered'].includes(status)) {
      timeline.push({
        stage: 'identified',
        icon: 'users',
        color: 'teal',
        title: 'Participants Identified',
        timestamp: meeting.identifiedAt || meeting.updatedAt,
        details: {
          'Participants': meeting.participants?.length > 0
            ? meeting.participants.map(p => p.email || p.name).join(', ')
            : (meeting.validatedParticipants?.length > 0
                ? meeting.validatedParticipants.join(', ')
                : 'N/A'),
          'Participant Count': meeting.participants?.length || meeting.validatedParticipants?.length || 0,
          'Assignment Status': meeting.assignmentStatus || 'N/A'
        }
      });
    }

    // 7. Triage Queue
    if (status === 'needs_triage' || ['assigned', 'analyzing', 'delivered', 'completed'].includes(status)) {
      timeline.push({
        stage: 'needs_triage',
        icon: 'attention',
        color: status === 'needs_triage' ? 'orange' : 'grey',
        title: status === 'needs_triage' ? 'Awaiting Triage (Current)' : 'Added to Triage Queue',
        timestamp: meeting.triageReadyAt || meeting.updatedAt,
        details: {
          'Status': status === 'needs_triage' ? 'Waiting for admin review' : 'Processed',
          'Triage Queue Position': meeting.triagePosition || 'N/A'
        }
      });
    }

    // 8. Assigned
    if (meeting.assignedTo || ['assigned', 'analyzing', 'delivered', 'completed'].includes(status)) {
      timeline.push({
        stage: 'assigned',
        icon: 'user',
        color: status === 'assigned' ? 'blue' : 'grey',
        title: status === 'assigned' ? 'Assigned to User (Current)' : 'Assigned to User',
        timestamp: meeting.assignedAt || meeting.updatedAt,
        details: {
          'Assigned To': meeting.assignedTo || 'N/A',
          'Assigned By': meeting.assignedBy || 'Admin'
        }
      });
    }

    // 9. Analysis Started
    if (['analyzing', 'delivered', 'completed'].includes(status)) {
      timeline.push({
        stage: 'analyzing',
        icon: 'lightbulb',
        color: status === 'analyzing' ? 'yellow' : 'grey',
        title: status === 'analyzing' ? 'AI Analysis in Progress (Current)' : 'AI Analysis Completed',
        timestamp: meeting.analysisStartedAt || meeting.updatedAt,
        details: {
          'Analysis Type': 'Sales Conversation Analysis',
          'AI Model': 'Claude (Anthropic)',
          'Status': status === 'analyzing' ? 'In Progress' : 'Completed'
        }
      });
    }

    // 10. Delivered
    if (status === 'delivered' || status === 'completed') {
      timeline.push({
        stage: 'delivered',
        icon: 'send',
        color: 'green',
        title: 'Results Delivered',
        timestamp: meeting.deliveredAt || meeting.updatedAt,
        details: {
          'Delivered To': meeting.assignedTo || 'User',
          'Total Processing Time': this.calculateDuration(meeting.createdAt, meeting.deliveredAt || meeting.updatedAt),
          'Insights Generated': 'Available in Triage Dashboard'
        }
      });
    }

    // 11. Failed
    if (status === 'failed') {
      timeline.push({
        stage: 'failed',
        icon: 'warning sign',
        color: 'red',
        title: 'Processing Failed',
        timestamp: meeting.failedAt || meeting.updatedAt,
        details: {
          'Error': meeting.errorMessage || meeting.lastError || 'Unknown error',
          'Failed Stage': meeting.failedStage || meeting.currentStage || 'Unknown',
          'Retry Count': (meeting.metadata && meeting.metadata.retryCount) || 0,
          'Can Retry': ((meeting.metadata && meeting.metadata.retryCount) || 0) < 3 ? 'Yes' : 'Max retries exceeded'
        }
      });
    }

    // 12. Stuck/Stalled Detection
    // If status is "completed" but no transcription evidence, the meeting is stuck
    if (status === 'completed' &&
        !meeting.transcriptionJobName &&
        !meeting.transcriptionStartedAt &&
        !meeting.transcriptionCompletedAt) {
      timeline.push({
        stage: 'stuck',
        icon: 'pause circle',
        color: 'orange',
        title: 'Pipeline Stalled',
        timestamp: meeting.updatedAt,
        details: {
          'Issue': 'Recording completed but pipeline did not progress',
          'Likely Cause': 'SQS permission error or queue delivery failure',
          'Recommendation': 'Check CloudWatch logs and manually retry',
          'Recording URL Available': meeting.metadata?.recordingUrl ? 'Yes (may be expired)' : 'No'
        }
      });
    }

    return timeline;
  };

  calculateDuration = (start, end) => {
    if (!start || !end) return 'N/A';
    const ms = new Date(end) - new Date(start);
    const minutes = Math.floor(ms / 1000 / 60);
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  getStageColor = (stage) => {
    const colors = {
      scheduled: 'blue',
      recording: 'red',
      transcription: 'purple',
      identified: 'teal',
      needs_triage: 'orange',
      assigned: 'blue',
      analyzing: 'yellow',
      delivered: 'green',
      failed: 'red'
    };
    return colors[stage] || 'grey';
  };

  render() {
    const { loading, meeting, timeline, error } = this.state;

    return (
      <div style={componentContainerStyle}>
        <MultiplyHeader header="Meeting Trace" />
        
        <div style={componentContentStyle}>
          <Segment>
            <Button as={Link} to="/murphy/meetings" icon labelPosition='left' size='small'>
              <Icon name='arrow left' />
              Back to Meetings
            </Button>
          </Segment>

          {loading && (
            <Segment>
              <Loader active inline='centered' content='Loading meeting trace...' />
            </Segment>
          )}

          {error && (
            <Message negative>
              <Message.Header>Error Loading Meeting</Message.Header>
              <p>{error}</p>
            </Message>
          )}

          {!loading && !error && meeting && (
            <>
              {/* Meeting Summary */}
              <Segment>
                <Header as='h3'>
                  <Icon name='info circle' />
                  Meeting Details
                </Header>
                <Table definition>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell width={4}>Bot ID</Table.Cell>
                      <Table.Cell><code>{meeting.id || meeting.botId}</code></Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>Meeting Title</Table.Cell>
                      <Table.Cell>{meeting.meetingTitle || 'N/A'}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>Status</Table.Cell>
                      <Table.Cell>
                        <Label color={this.getStageColor(meeting.status)}>
                          {meeting.status}
                        </Label>
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>Recall Bot ID</Table.Cell>
                      <Table.Cell>{meeting.recallBotId || 'N/A'}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>Meeting URL</Table.Cell>
                      <Table.Cell>
                        {meeting.meetingUrl ? (
                          <a href={meeting.meetingUrl} target="_blank" rel="noopener noreferrer">
                            {meeting.meetingUrl}
                          </a>
                        ) : 'N/A'}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>Participants</Table.Cell>
                      <Table.Cell>
                        {meeting.validatedParticipants?.length > 0 
                          ? meeting.validatedParticipants.join(', ') 
                          : 'N/A'}
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table>
              </Segment>

              {/* Processing Timeline */}
              <Segment>
                <Header as='h3'>
                  <Icon name='history' />
                  Processing Timeline
                </Header>
                
                {timeline.length === 0 ? (
                  <Message info>
                    <Message.Header>No Timeline Data</Message.Header>
                    <p>Timeline information is not available for this meeting.</p>
                  </Message>
                ) : (
                  <div style={{ paddingLeft: '20px' }}>
                    {timeline.map((step, index) => (
                      <Segment key={index} style={{ marginBottom: '20px', borderLeft: `4px solid ${step.color}` }}>
                        <Header as='h4'>
                          <Icon name={step.icon} color={step.color} />
                          {step.title}
                          <Header.Subheader>
                            {this.formatTimestamp(step.timestamp)}
                          </Header.Subheader>
                        </Header>
                        
                        <Table basic='very' compact>
                          <Table.Body>
                            {Object.entries(step.details).map(([key, value]) => (
                              <Table.Row key={key}>
                                <Table.Cell width={4} style={{ fontWeight: 'bold', color: '#666' }}>
                                  {key}:
                                </Table.Cell>
                                <Table.Cell>{value || 'N/A'}</Table.Cell>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table>
                      </Segment>
                    ))}
                  </div>
                )}
              </Segment>

              {/* Raw Data (for debugging) */}
              <Segment>
                <Header as='h4'>
                  <Icon name='code' />
                  Raw Meeting Data
                </Header>
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: '15px', 
                  borderRadius: '4px',
                  overflow: 'auto',
                  fontSize: '12px'
                }}>
                  {JSON.stringify(meeting, null, 2)}
                </pre>
              </Segment>
            </>
          )}
        </div>
      </div>
    );
  }
}

export default withRouter(observer(MeetingTracePage));
